import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, type Kpi } from '../api.js';
import Icon from '../components/Icon.js';

/**
 * Per-campaign report — a page meant to be printed and passed on.
 *
 * Three families of indicators, with different readers:
 *   TELECOM     — ASR/NER/ACD/PDD, the language in which TMT talks to their partners.
 *   VALIDATION  — how many numbers exist. Their actual product.
 *   OPERATIONS  — throughput, duration, cost. For whoever runs the campaign.
 *
 * The figures stand as values, not as charts: an ASR of 42% does not become
 * clearer inside a gauge. A chart is drawn only where composition matters — the
 * distribution of verdicts and that of the delays.
 */

/**
 * The colour order is FIXED and by role, not by frequency: if you filter or
 * sort differently, `ANSWERED` stays blue. The palette is the validated one
 * (adjacent CVD ΔE 8.4 dark / 9.1 light, normal vision 19.3 / 19.6).
 *
 * Deliberately NOT semantic red-for-bad colours: for number validation,
 * `NOT_ALLOCATED` is not a failure, it is exactly the result being sought.
 */
const SERIES: Record<string, string> = {
  ANSWERED: 'var(--s1)', NOT_ALLOCATED: 'var(--s2)', PHONE_OFF: 'var(--s3)',
  NO_ANSWER: 'var(--s4)', BUSY_OR_REJECTED: 'var(--s5)',
  NETWORK_ERROR: 'var(--s6)', UNKNOWN: 'var(--s7)', PENDING: 'var(--s7)',
};

export default function Report() {
  const { id } = useParams();
  const [k, setK] = useState<Kpi | null>(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.kpi(Number(id)).then(setK).catch((e) => setErr(e.message));
  }, [id]);

  if (err) return <div className="err">{err}</div>;
  if (!k) return <div className="empty">Loading…</div>;

  const maxDist = Math.max(1, ...k.distribution.map((d) => d.n));
  const maxPdd = Math.max(1, ...k.pddBuckets.map((b) => b.n));

  return (
    <div className="viz-root report">
      <div className="spread">
        <div>
          <h1>Report · {k.campaign.name}</h1>
          <p className="subtitle">
            {k.campaign.country} · {k.volume.called} numbers tested out of {k.volume.total}
            {k.volume.skipped > 0 && ` · ${k.volume.skipped} rejected at import`}
            {' · '}{new Date().toLocaleDateString('en-GB')}
          </p>
        </div>
        <button className="no-print" onClick={() => window.print()}><Icon name="print" />Print</button>
      </div>

      {/* ── Validation: the result the platform exists for ─────────────────── */}
      <h2>Number validation</h2>
      <div className="hero">
        <div className="hero-n">{k.validation.validityRate}%</div>
        <div className="hero-k">
          numbers active in the network — {k.validation.active} of {k.volume.called} tested
        </div>
      </div>
      <div className="cards">
        <div className="stat"><div className="n">{k.validation.notAllocated}</div>
          <div className="k">numbers not allocated</div></div>
        <div className="stat"><div className="n">{k.validation.phoneOff}</div>
          <div className="k">phone off</div></div>
        <div className="stat"><div className="n">{k.volume.withEarlyMedia}</div>
          <div className="k">with early media recorded</div></div>
      </div>

      {/* ── Telecom: the common language with carriers ─────────────────────── */}
      <h2>Telecom indicators</h2>
      <div className="cards">
        <div className="stat" title="Answer Seizure Ratio — answered out of all attempts">
          <div className="n">{k.telecom.asr}%</div><div className="k">ASR</div></div>
        <div className="stat" title="Network Efficiency Ratio — the network delivered the call, whatever the subscriber did">
          <div className="n">{k.telecom.ner}%</div><div className="k">NER</div></div>
        <div className="stat" title="Average Call Duration, over answered calls only">
          <div className="n">{k.telecom.acd}s</div><div className="k">ACD</div></div>
        <div className="stat" title="Median post-dial delay — INVITE to early media">
          <div className="n">{k.telecom.pddMedian ?? '—'}<small>ms</small></div>
          <div className="k">PDD median</div></div>
        <div className="stat" title="Post-dial delay, 95th percentile">
          <div className="n">{k.telecom.pddP95 ?? '—'}<small>ms</small></div>
          <div className="k">PDD p95</div></div>
      </div>

      {/* ── Composition: the only place where a chart says more ────────────── */}
      <h2>Distribution of verdicts</h2>
      <div className="chart">
        {k.distribution.map((d) => (
          <div key={d.disposition} className="bar-row" title={`${d.disposition}: ${d.n} (${d.percent}%)`}>
            <div className="bar-label">
              <span className="swatch" style={{ background: SERIES[d.disposition] ?? 'var(--s7)' }} />
              {d.disposition}
            </div>
            <div className="bar-track">
              <div className="bar" style={{ width: `${(d.n / maxDist) * 100}%`,
                                            background: SERIES[d.disposition] ?? 'var(--s7)' }} />
            </div>
            <div className="bar-value">{d.n} <span className="muted">· {d.percent}%</span></div>
          </div>
        ))}
      </div>

      <h2>Post-dial delay</h2>
      <p className="subtitle" style={{ marginTop: -4 }}>
        The time from INVITE to the first audio signal. Over 5 seconds indicates poor routing.
      </p>
      <div className="chart">
        {k.pddBuckets.map((b) => (
          <div key={b.bucket} className="bar-row" title={`${b.bucket}: ${b.n}`}>
            <div className="bar-label">{b.bucket}</div>
            <div className="bar-track">
              {/* Buckets = ordered magnitude, so a single hue, not categories. */}
              <div className="bar seq" style={{ width: `${(b.n / maxPdd) * 100}%` }} />
            </div>
            <div className="bar-value">{b.n}</div>
          </div>
        ))}
      </div>

      <h2>SIP codes received</h2>
      <div className="table-wrap">
        <table>
          <thead><tr><th>Code</th><th>Reason reported by the network</th><th>Calls</th></tr></thead>
          <tbody>
            {k.sipCodes.map((s) => (
              <tr key={String(s.code)}>
                <td className="num">{s.code ?? <span className="muted">—</span>}</td>
                <td>{s.reason ?? <span className="muted">no reason</span>}</td>
                <td className="num">{s.n}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2>Operations</h2>
      <div className="cards">
        <div className="stat"><div className="n">{k.operations.durationMinutes ?? '—'}<small>min</small></div>
          <div className="k">campaign duration</div></div>
        <div className="stat"><div className="n">{k.operations.actualCallsPerMinute ?? '—'}</div>
          <div className="k">calls / minute achieved</div></div>
        <div className="stat"><div className="n">{k.operations.networkErrorRate}%</div>
          <div className="k">network errors</div></div>
        <div className="stat"><div className="n">{k.operations.totalCost.toFixed(2)} €</div>
          <div className="k">total cost</div></div>
        <div className="stat"><div className="n">{k.operations.costPerNumberTested.toFixed(4)} €</div>
          <div className="k">per number tested</div></div>
      </div>

      <p className="muted" style={{ marginTop: 26, fontSize: 12 }}>
        Billing starts at the answer — ringing is not charged. The verdicts are based on the
        SIP signalling corroborated with the shape of the audio signal from early media; the
        reason for each classification is available on every row of the campaign detail.
      </p>
    </div>
  );
}
